import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { Dropdown } from "react-bootstrap";
import { apiFetch } from '../../apiClient';
import axios from 'axios';
import { PendingOrderContext } from '../orders/PendingOrderProvider';
import { getSeasonUrl } from '../helpers/shareHelpers';

function SeasonSelect() {
    const [seasons, setSeasons] = useState([]);
    const [checkedSeasons, setCheckedSeasons] = useState({});
    const [loadingChecked, setLoadingChecked] = useState(true);
    const [playerDetails, setPlayerDetails] = useState(null);

    // Get the refresh function from context.
    const { refreshPendingOrder } = useContext(PendingOrderContext);

    // Fetch player details
    useEffect(() => {
        async function fetchPlayerDetails() {
            try {
                const response = await apiFetch('/api/player/detail/');
                if (!response.ok) {
                    throw new Error('Failed to fetch player details');
                }
                const data = await response.json();
                setPlayerDetails(data);
            } catch (error) {
                console.error('Error fetching player details:', error);
            }
        }
        fetchPlayerDetails();
    }, []);

    // Fetch pending order items to set the initial checkbox state.
    useEffect(() => {
        async function fetchPendingOrderItems() {
            try {
                const response = await apiFetch('/api/orders/my-pending/');
                if (!response.ok) {
                    throw new Error('Failed to fetch pending order');
                }
                const data = await response.json();
                const order = data.order;
                if (order && order.items && order.items.length > 0) {
                    const newChecked = {};
                    order.items.forEach(item => {
                        if (item.product && item.product.id) {
                            newChecked[item.product.id] = true;
                        }
                    });
                    setCheckedSeasons(newChecked);
                } else {
                    setCheckedSeasons({});
                }
            } catch (error) {
                console.error('Error fetching pending order items:', error);
            } finally {
                setLoadingChecked(false);
            }
        }
        fetchPendingOrderItems();
    }, []);

    // Handler for toggling a season's checkbox.
    const handleCheckboxChange = async (seasonId) => {
        // Optimistically update local state immediately.
        const isCurrentlyChecked = !!checkedSeasons[seasonId];

        if (!isCurrentlyChecked) {
            try {
                const response = await apiFetch('/api/orders/add-season/', {
                    method: 'POST',
                    body: JSON.stringify({ season_id: seasonId }),
                });
                if (!response.ok) {
                    const errData = await response.json();
                    throw new Error(errData.error || 'Error adding season to order');
                }
                setCheckedSeasons(prevState => ({
                    ...prevState,
                    [seasonId]: true,
                }));
                // Refresh the pending order (and hence the header badge).
                if (refreshPendingOrder) refreshPendingOrder();
            } catch (error) {
                console.error('Error adding season to order:', error);
            }
        } else {
            try {
                const response = await apiFetch('/api/orders/remove-season/', {
                    method: 'POST',
                    body: JSON.stringify({ season_id: seasonId }),
                });
                if (!response.ok) {
                    const errData = await response.json();
                    throw new Error(errData.error || 'Error removing season from order');
                }
                setCheckedSeasons(prevState => ({
                    ...prevState,
                    [seasonId]: false,
                }));
                if (refreshPendingOrder) refreshPendingOrder();
            } catch (error) {
                console.error('Error removing season from order:', error);
            }
        }
    };

    // Fetch seasons with open registrations using axios.
    useEffect(() => {
        axios.get('/api/leagues/seasons/open_registrations/', {
            headers: {
                Authorization: `Bearer ${localStorage.getItem('access_token')}`,
            },
        })
            .then(response => {
                setSeasons(response.data);
            })
            .catch(error => {
                console.error('Error fetching seasons:', error);
            });
    }, []);


    return (
        <div className="container p-0">
            <div className="card h-100">
                {/* Card Header */}
                <div className="card-header d-sm-flex align-items-center text-center justify-content-sm-between border-0 pb-0">
                    <h1 className="h4 card-title">Sign up For a Season</h1>
                </div>
                {/* Card Body */}
                <div className="card-body">
                    <div className="container">
                        {/* Optionally, you can show a loading indicator while the checked state is loading */}
                        {loadingChecked ? (
                            <p>Loading your selections...</p>
                        ) : (
                            <div className="row g-4">
                                {seasons.map((season) => (

                                    <div key={season.id} className="col-sm-6 col-xl-4">
                                        <div className="card h-100">
                                            <div className="position-relative">
                                                <img
                                                    className="img-fluid rounded-top"
                                                    src={
                                                        season.league_logo ||
                                                        `/static/images/kb_static/lemont_rec_leagues_circle.png`
                                                    }
                                                    alt=""
                                                    style={{
                                                        width: '200px',
                                                        height: '200px',
                                                        objectFit: 'cover',
                                                        margin: 'auto',
                                                        display: 'block',
                                                    }}
                                                />
                                                <div className="badge bg-danger text-white mt-2 me-2 position-absolute top-0 end-0">
                                                    Open
                                                </div>
                                            </div>
                                            <div className="card-body position-relative pt-0">
                                                <div className="btn btn-xs btn-primary mt-n3">
                                                    {season.league_name}
                                                </div>
                                                <h6 className="mt-3">
                                                    <Link className="nav-link" to={`/leagues/seasons/my-season/${season.id}`}>
                                                        {season.name}
                                                    </Link>
                                                </h6>
                                                <p className="mb-0 small">
                                                    <i className="bi bi-calendar-check pe-1"></i> {season.formatted_registration_start_date}
                                                </p>
                                                <p className="small">
                                                    <i className="bi bi-geo-alt pe-1"></i> {season.league_location}
                                                </p>
                                                <ul className="avatar-group list-unstyled align-items-center mb-0">
                                                    {(() => {
                                                        const regs = season.participants;
                                                        const shown = regs.slice(0, 3);
                                                        const more = regs.length - shown.length;

                                                        return (
                                                            <>
                                                                {shown.map((p, i) => (
                                                                    <li key={`${p.id}-${i}`} className="avatar avatar-xs">
                                                                        <img
                                                                            className="avatar-img rounded-circle"
                                                                            src={
                                                                                p.profile_picture ||
                                                                                `/static/images/kb_static/lemont_rec_leagues_circle.png`
                                                                            }
                                                                            alt={`${p.first_name} ${p.last_name}`}
                                                                        />
                                                                    </li>
                                                                ))}

                                                                {more > 0 && (
                                                                    <li className="avatar avatar-xs">
                                                                        <div className="avatar-img rounded-circle bg-primary">
                                                                            <span className="smaller text-white position-absolute top-50 start-50 translate-middle">
                                                                                +{more}
                                                                            </span>
                                                                        </div>
                                                                    </li>
                                                                )}

                                                                <li className="ms-3">
                                                                    <small>
                                                                        {regs.length}{' '}
                                                                        {regs.length === 1 ? 'player' : 'players'}
                                                                    </small>
                                                                </li>
                                                            </>
                                                        );
                                                    })()}
                                                </ul>
                                                <div className="d-flex mt-3 justify-content-between">
                                                    {(() => {
                                                        const alreadyRegistered =
                                                            !!playerDetails &&
                                                            season.participants.some((p) => p.id === playerDetails.id);

                                                        if (alreadyRegistered) {
                                                            return (
                                                                <div className="w-100">
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-secondary d-block w-100"
                                                                        disabled
                                                                    >
                                                                        <i className="fa-solid fa-check me-1" aria-hidden="true"></i>
                                                                        Registered
                                                                    </button>
                                                                </div>
                                                            );
                                                        }

                                                        if (season.registration_open) {
                                                            return (
                                                                <div className="w-100">
                                                                    <a
                                                                        href={`/league/${season.id}/register/`}
                                                                        className="btn btn-sm btn-success d-block"
                                                                    >
                                                                        <i className="fa-solid fa-pen-to-square me-1" aria-hidden="true"></i>
                                                                        Register
                                                                    </a>
                                                                </div>
                                                            );
                                                        }

                                                        return (
                                                            <div className="w-100">
                                                                <input
                                                                    type="checkbox"
                                                                    className="btn-check"
                                                                    id={`Interested${season.id}`}
                                                                    checked={!!checkedSeasons[season.id]}
                                                                    onChange={() => handleCheckboxChange(season.id)}
                                                                />
                                                                <label
                                                                    className="btn btn-sm btn-outline-success d-block"
                                                                    htmlFor={`Interested${season.id}`}
                                                                >
                                                                    <i className="fa-solid fa-thumbs-up me-1" aria-hidden="true"></i>
                                                                    Interested
                                                                </label>
                                                            </div>
                                                        );
                                                    })()}
                                                    <Dropdown className="ms-3">
                                                        <Dropdown.Toggle
                                                            variant="btn btn-sm btn-primary-soft"
                                                            id={`shareMenu${season.id}`}
                                                            className="custom-dropdown-toggle"
                                                        >
                                                            <i className="bi bi-share-fill"></i>
                                                        </Dropdown.Toggle>

                                                        <Dropdown.Menu align="end">
                                                            <Dropdown.Item
                                                                onClick={() => {
                                                                    const url = getSeasonUrl(season.id);
                                                                    navigator.clipboard.writeText(url);
                                                                    alert('Link copied to clipboard');
                                                                }}
                                                            >
                                                                <i className="bi bi-clipboard fa-fw pe-1"></i> Copy link
                                                            </Dropdown.Item>

                                                            <Dropdown.Item
                                                                disabled={!('share' in navigator)}
                                                                onClick={async () => {
                                                                    try {
                                                                        await navigator.share({
                                                                            title: `${season.name} – ${season.league_name}`,
                                                                            text: 'Sign up for this Kickball season!',
                                                                            url: getSeasonUrl(season.id),
                                                                        });
                                                                    } catch { }
                                                                }}
                                                            >
                                                                <i className="bi bi-share-fill fa-fw pe-1"></i> Share…
                                                            </Dropdown.Item>
                                                        </Dropdown.Menu>
                                                    </Dropdown>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default SeasonSelect;